/**
 * Flag-gated persistent-entitlement cache buster — the sibling of `withCacheBust`
 * (media) in feature-flags.ts, applied to the React Query IndexedDB snapshot
 * instead of to media URLs.
 *
 * Why this exists: the persister in `main.tsx` restores a snapshot of
 * entitlement-sensitive queries (tier-gated `community-channels`, `alfie-*`)
 * from IndexedDB across sessions, and its `buster: VITE_BUILD_VERSION` only
 * changes on DEPLOY. Entitlements change between deploys (a backfill, an admin
 * tier/add-on change), so the stale snapshot survives and the member is locked
 * out of access they now have (backend correct, UI stale). This gives ops a
 * lever to flush that snapshot without a redeploy.
 *
 * Behaviour: when `entitlement_cache_bust` is enabled with a non-empty `value`,
 * drop the persisted snapshot (and reset the in-memory cache) exactly ONCE per
 * distinct value on each device — so entitlement-sensitive queries refetch
 * fresh. Bump the `value` in admin/flags after a backfill to re-bust globally.
 * Disabled or empty value → no-op (identical to having no flag).
 *
 * Wired in the root route `beforeLoad`, right after `loadFeatureFlags`, so the
 * sync flag cache is hot and the flush lands before any authed route paints.
 */

import type { QueryClient } from '@tanstack/react-query';
import { del as idbDel } from 'idb-keyval';
import { getFlag } from '@/lib/feature-flags';

/** Must match the persister `key` in main.tsx. */
export const PERSIST_KEY = 'fb-message-cache';
/** Per-device record of the last flag value we've already busted for. */
const SEEN_MARKER = 'fb-entitlement-bust';

export async function bustEntitlementCacheIfNeeded(queryClient: QueryClient): Promise<void> {
  const flag = getFlag('entitlement_cache_bust');
  if (!flag.enabled || !flag.value) return;

  let seen: string | null = null;
  try {
    seen = localStorage.getItem(SEEN_MARKER);
  } catch {
    // storage unavailable (private mode / SSR) — fall through and attempt the bust
  }
  if (seen === flag.value) return; // already flushed for this value on this device

  try {
    // Kill the persisted snapshot so PersistQueryClientProvider can't rehydrate
    // stale entitlements, and reset the in-memory cache so anything already
    // restored is dropped. Queries refetch fresh on next mount.
    await idbDel(PERSIST_KEY);
    queryClient.clear();
  } catch (err) {
    // Never block navigation on a bust failure; retry on the next load (marker
    // not written, so the value still looks "unseen").
    console.error('[entitlement-cache-bust] flush failed', err);
    return;
  }

  try {
    localStorage.setItem(SEEN_MARKER, flag.value);
  } catch {
    // If we can't record it, we'll re-flush next load — harmless (idempotent).
  }
}
